import { useMemo } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info } from 'lucide-react';
import { useGetFeedTeamsQuery } from '@/api/feed';
import type { QuestionType, FixtureSelection } from '../QuestionCoachModal';

interface MainMarketStepProps {
  questionType: QuestionType;
  /** Favourite team name (spread only) — for the line label. Legacy single-fixture mode. */
  favouriteTeamName?: string;
  margin?: number;
  onMarginChange?: (margin: number | undefined) => void;
  /**
   * Batch creation mode (DKPLAT-501 Phase 1): one card per selected fixture.
   * Spread's line is keyed by the fixture's favourite team ID; Total's line
   * (match-level, no team) is keyed by the fixture ID itself.
   */
  fixtures?: FixtureSelection[];
  leagueId?: string;
  onThresholdChange?: (fixtureId: string, key: string, margin: number | undefined) => void;
}

const isHalfPointLine = (margin?: number) =>
  margin !== undefined && margin > 0 && margin % 1 === 0.5;

/**
 * Step 5 for the Main Markets (DKPLAT-500). Moneyline needs no input (graded on the
 * final result); Spread and Total take a single half-point LINE. Half-point lines are
 * enforced (e.g. 3.5, 45.5) so a push is impossible.
 */
export function MainMarketStep({
  questionType,
  favouriteTeamName,
  margin,
  onMarginChange,
  fixtures,
  leagueId,
  onThresholdChange,
}: MainMarketStepProps) {
  const { data: teams } = useGetFeedTeamsQuery(fixtures ? leagueId : undefined);

  const teamMap = useMemo(() => {
    const map = new Map<string, string>();
    teams?.forEach(team => map.set(team.externalId, team.name));
    return map;
  }, [teams]);

  if (questionType === 'moneyline') {
    return (
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          Moneyline questions are graded automatically from the final result — no line to set
          {fixtures
            ? ` for the ${fixtures.length} selected fixture${fixtures.length === 1 ? '' : 's'}`
            : ''}
          . Continue to review.
        </AlertDescription>
      </Alert>
    );
  }

  const isSpread = questionType === 'spread';
  const placeholder = isSpread ? 'e.g., 3.5' : 'e.g., 2.5';
  const hint = isSpread
    ? 'Points the favourite must win by to cover (half-point, e.g. 3.5).'
    : 'Combined score over/under line (half-point, e.g. 2.5).';

  // Batch creation mode (DKPLAT-501 Phase 1): one card per fixture.
  if (fixtures) {
    return (
      <div className="space-y-4">
        {fixtures.map(fixture => {
          // Spread: keyed by the favourite team ID. Total: match-level, keyed by fixtureId.
          const key = isSpread ? fixture.selectedTeamIds[0] : fixture.fixtureId;
          const fixtureMargin = key ? fixture.thresholds[key] : undefined;
          const showError = fixtureMargin !== undefined && !isHalfPointLine(fixtureMargin);
          const label = isSpread
            ? `Handicap line for ${teamMap.get(key || '') || 'the favourite'}`
            : 'Total line';

          return (
            <Card key={fixture.fixtureId} className="p-4 space-y-2">
              <Label className="text-sm font-semibold">{fixture.fixtureDisplay}</Label>
              <Label htmlFor={`main-market-line-${fixture.fixtureId}`} className="text-sm">
                {label}
              </Label>
              <Input
                id={`main-market-line-${fixture.fixtureId}`}
                data-testid={`main-market-line-input-${fixture.fixtureId}`}
                type="number"
                step={0.5}
                min={0.5}
                value={fixtureMargin ?? ''}
                placeholder={placeholder}
                disabled={isSpread && !key}
                onChange={e => {
                  if (!key) return;
                  const v = e.target.value;
                  onThresholdChange?.(fixture.fixtureId, key, v === '' ? undefined : Number(v));
                }}
              />
              {showError && (
                <p className="text-sm text-destructive">
                  Line must be a positive half-point number (e.g. 2.5, 3.5, 45.5).
                </p>
              )}
            </Card>
          );
        })}
        <p className="text-sm text-muted-foreground">{hint}</p>
      </div>
    );
  }

  const label = isSpread
    ? `Handicap line for ${favouriteTeamName || 'the favourite'}`
    : 'Total line';

  // Valid only when present, positive and a half-point (ends in .5) — no pushes.
  const showError = margin !== undefined && !isHalfPointLine(margin);

  return (
    <div className="space-y-2">
      <Label htmlFor="main-market-line">{label}</Label>
      <Input
        id="main-market-line"
        data-testid="main-market-line-input"
        type="number"
        step={0.5}
        min={0.5}
        value={margin ?? ''}
        placeholder={placeholder}
        onChange={e => {
          const v = e.target.value;
          onMarginChange?.(v === '' ? undefined : Number(v));
        }}
      />
      <p className="text-sm text-muted-foreground">{hint}</p>
      {showError && (
        <p className="text-sm text-destructive">
          Line must be a positive half-point number (e.g. 2.5, 3.5, 45.5).
        </p>
      )}
    </div>
  );
}
